/**
 * The numbers the revenue-leak KPI screen is allowed to use.
 *
 * Everything starts blank on purpose. The engine will not price a leak from a
 * figure nobody agreed to, so an empty field is not a missing default — it
 * switches its drivers off, and the KPI screen says which ones and why.
 *
 * Two scopes, shown as two badges. The leak rates are one value for the whole
 * organisation: "4% of charges go unposted" describes paper billing, not a
 * product. What a patient is worth is per product, because a clinic OP visit is
 * worth roughly half a hospital's and one figure across both overstated one of
 * them. Anything that varies between individual hospitals — their beds, their
 * OP volume — is typed on the KPI screen instead, which is why there is no
 * geography here.
 *
 * The greyed number in each box is a published benchmark, shown for reference.
 * It is never used until someone types it in. That is the difference between
 * "our number" and "some number", and it is why this screen exists.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Save, Check, AlertTriangle, RotateCcw, Globe, Stethoscope } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { LEAK_SETTINGS_URL } from "../../constants/api";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";

const UNIT_SUFFIX = { pct: "%", inr: "₹", days: "days", minutes: "min" };

export default function LeakAssumptions() {
    const { fetch, post } = useApiMethods();

    const [rows, setRows]         = useState([]);
    const [groups, setGroups]     = useState([]);
    const [products, setProducts] = useState([]);
    const [product, setProduct]   = useState("");
    const [draft, setDraft]       = useState({});
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);

    const absorb = useCallback((res) => {
        setRows(res.data || []);
        setGroups(res.groups || []);
        setProducts(res.products || []);
        if (res.product) setProduct(res.product);
        setDraft(Object.fromEntries(
            (res.data || []).map(r => [r.key, r.value == null ? "" : String(r.value)]),
        ));
    }, []);

    const load = useCallback((prod) => {
        setLoading(true);
        fetch(prod ? `${LEAK_SETTINGS_URL}?product=${prod}` : LEAK_SETTINGS_URL)
            .then(res => {
                if (res?.status === "success") absorb(res);
                else ToastMsg(res?.message || "Could not load the assumptions", "error");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [absorb]);

    useEffect(() => { load(""); }, []);        // eslint-disable-line react-hooks/exhaustive-deps

    const dirty = useMemo(
        () => rows.some(r => (r.value == null ? "" : String(r.value)) !== (draft[r.key] ?? "")),
        [rows, draft],
    );

    // Switching product loads a different set of values, so a half-typed edit
    // would be silently thrown away. Ask first.
    const switchTo = (prod) => {
        if (prod === product) return;
        if (dirty && !window.confirm("Unsaved changes here will be lost. Switch anyway?")) return;
        setProduct(prod);
        load(prod);
    };

    const save = async () => {
        setSaving(true);
        try {
            const res = await post(LEAK_SETTINGS_URL, { values: draft, product });
            if (res?.status === "success") { absorb(res); ToastMsg("Saved", "success"); }
            else ToastMsg(res?.message || "Could not save", "error");
        } finally { setSaving(false); }
    };

    const revert = () => setDraft(Object.fromEntries(
        rows.map(r => [r.key, r.value == null ? "" : String(r.value)]),
    ));

    const scopedRows = rows.filter(r => r.scope === "product");
    const scopedSet  = scopedRows.filter(r => r.is_set).length;
    const globalRows = rows.filter(r => r.scope === "global");
    const globalSet  = globalRows.filter(r => r.is_set).length;
    const productName = products.find(p => p.key === product)?.label || "this product";

    if (loading && !rows.length) {
        return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="px-6 py-5 space-y-5 max-w-5xl">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-lg font-semibold text-foreground">Leak assumptions</h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Every figure the KPI screen quotes to a hospital comes from this page.
                        Nothing is assumed on your behalf — a blank field switches its leaks
                        off rather than falling back to a number we picked.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {dirty && (
                        <Button size="sm" variant="outline" onClick={revert}>
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Revert
                        </Button>
                    )}
                    <Button size="sm" onClick={save} disabled={saving || !dirty}>
                        <Save className="h-3.5 w-3.5 mr-1" />{saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>

            {/* ── which product am I editing ─────────────────────────────── */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <label className="block text-[11px] text-muted-foreground mb-1">
                            Editing figures for
                        </label>
                        <div className="flex rounded-lg border border-border overflow-hidden">
                            {products.map(p => (
                                <button key={p.key} onClick={() => switchTo(p.key)}
                                    className={cn("h-9 px-4 text-sm",
                                        product === p.key
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-background text-muted-foreground hover:text-foreground")}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <p className="text-[11px] text-muted-foreground">
                    The product comes off the quotation itself — a plan priced as CMS is a
                    clinic, HIMS is a hospital — so the KPI screen always uses the figures
                    matching what was actually sold. Fields marked{" "}
                    <Stethoscope className="inline h-3 w-3" /> apply only to{" "}
                    {productName}; fields marked <Globe className="inline h-3 w-3" /> apply
                    to both.
                </p>
            </div>

            <div className={cn("rounded-lg border p-3 text-xs flex items-start gap-2",
                scopedSet === scopedRows.length && globalSet === globalRows.length
                    ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/15"
                    : "border-amber-300 bg-amber-50/60 dark:bg-amber-900/15")}>
                {scopedSet === scopedRows.length && globalSet === globalRows.length
                    ? <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-px" />
                    : <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-px" />}
                <p className="text-foreground">
                    <strong>{scopedSet} of {scopedRows.length}</strong> set for {productName},{" "}
                    <strong>{globalSet} of {globalRows.length}</strong> set for both. Leaks
                    depending on a blank field do not appear on the KPI screen — they are
                    listed there as not counted, naming what they wait for.
                </p>
            </div>

            {groups.map(group => {
                const groupRows = rows.filter(r => r.group === group);
                if (!groupRows.length) return null;
                const scope = groupRows[0].scope;
                return (
                    <section key={group} className="rounded-lg border border-border bg-card">
                        <h2 className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider
                                       text-muted-foreground border-b border-border flex items-center gap-2">
                            {scope === "product"
                                ? <Stethoscope className="h-3.5 w-3.5" />
                                : <Globe className="h-3.5 w-3.5" />}
                            {group}
                            <span className="font-normal normal-case tracking-normal opacity-70">
                                {scope === "product" ? `— ${productName} only` : "— both products"}
                            </span>
                        </h2>
                        <div className="divide-y divide-border">
                            {groupRows.map(r => (
                                <div key={r.key} className="p-4 flex flex-col sm:flex-row gap-3 sm:gap-5">
                                    <div className="sm:w-2/3 min-w-0">
                                        <label htmlFor={r.key} className="text-sm font-medium text-foreground">
                                            {r.label}
                                        </label>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">{r.help}</p>
                                        {r.affects?.length > 0 && (
                                            <p className="text-[11px] text-muted-foreground mt-1">
                                                <span className="opacity-70">Switches on:</span>{" "}
                                                {r.affects.join(" · ")}
                                            </p>
                                        )}
                                        {r.note && (
                                            <p className="text-[11px] text-muted-foreground mt-1 italic">
                                                {r.note}
                                            </p>
                                        )}
                                    </div>
                                    <div className="sm:w-1/3 flex items-start gap-2">
                                        <div className="relative flex-1">
                                            <input
                                                id={r.key} type="number" min="0" step="any"
                                                value={draft[r.key] ?? ""}
                                                placeholder={String(r.suggestion)}
                                                onChange={e => setDraft(d => ({ ...d, [r.key]: e.target.value }))}
                                                className="w-full h-9 rounded-lg border border-border bg-background
                                                           px-3 pr-10 text-sm tabular-nums" />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2
                                                             text-[11px] text-muted-foreground pointer-events-none">
                                                {UNIT_SUFFIX[r.unit]}
                                            </span>
                                        </div>
                                        {/* One click to accept the benchmark — it still becomes
                                            *your* number the moment you save it. */}
                                        {(draft[r.key] ?? "") === "" && (
                                            <button type="button"
                                                onClick={() => setDraft(d => ({ ...d, [r.key]: String(r.suggestion) }))}
                                                className="h-9 px-2 rounded-lg border border-dashed border-border
                                                           text-[11px] text-muted-foreground hover:text-foreground
                                                           whitespace-nowrap">
                                                use {r.suggestion}{r.unit === "pct" ? "%" : ""}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}

            <p className="text-[11px] text-muted-foreground">
                Suggestions in grey are mid-points of published ranges for Indian private
                hospitals. They are shown for reference only and are never used in a
                calculation until you save them.
            </p>
        </div>
    );
}
